/**
 * Themeable colour values for use in inline `style` objects.
 *
 * ## Why this exists separately from `palette.ts`
 *
 * `palette.ts` is FROZEN. Its values carry domain meaning — MXI work-order
 * statuses, shift colours, priority codes, certificate expiry — and they must
 * look identical in every theme. Anything selecting a colour from a status
 * string keeps importing `palette`.
 *
 * This file is the opposite: plain UI chrome (surfaces, borders, body text,
 * the accent) that SHOULD follow the active theme. The values are
 * `var(--ds-*)` references, which work fine in inline styles, so the
 * components under `src/tailwindcomponents/` that build their base appearance
 * as a `style` object (a deliberate choice — see MIGRATION.md, it is what lets
 * a call site's own `style`/`className` win under `important: '#root'`) become
 * theme-aware by swapping this import for `palette`.
 *
 * Rule of thumb: if the colour answers "what kind of thing is this?" use
 * `palette`. If it answers "how does the UI look?" use this.
 */
export const cssVars = {
  primary: 'var(--ds-accent)',
  primaryHover: 'var(--ds-accent-hover)',
  primaryFg: 'var(--ds-accent-fg)',
  // Deliberately literal, matching palette.secondary/warning: this hex is also
  // the ACTV/COMMIT status colour, so a `color="secondary"` chip must not drift
  // away from a secondary-coloured status badge sitting next to it.
  secondary: '#F5B53F',
  accent: 'var(--ds-accent)',
  accentBlue: 'var(--ds-info)',
  error: 'var(--ds-danger)',
  warning: 'var(--ds-warn)',
  info: 'var(--ds-info)',
  success: 'var(--ds-ok)',
  background: 'var(--ds-bg)',
  backgroundPaper: 'var(--ds-surface)',
  surfaceSunken: 'var(--ds-surface-sunken)',
  surfaceHover: 'var(--ds-surface-hover)',
  border: 'var(--ds-border)',
  borderStrong: 'var(--ds-border-strong)',
  content: 'var(--ds-text)',
  muted: 'var(--ds-text-muted)',
  subtle: 'var(--ds-text-subtle)',
  focus: 'var(--ds-focus)',
  radiusSm: 'var(--ds-r-sm)',
  radiusMd: 'var(--ds-r-md)',
  radiusLg: 'var(--ds-r-lg)',
  shadow1: 'var(--ds-shadow-1)',
  shadow2: 'var(--ds-shadow-2)',
  shadow3: 'var(--ds-shadow-3)',
} as const;

export default cssVars;
